import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
  BackButton,
} from "../../../components/common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loader from "../../../components/partials/Loader";

const AddDeh = () => {
  const [loader, setLoader] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchDistricts = async () => {
      setLoader(true);
      try {
        const res = await fetchData(
          "districts",
          "GET",
          null,
          {},
          {},
          navigate,
          "/districts",
          dispatch
        );
        setDistricts(res?.data || []);
      } catch (error) {
        toast.error("Failed to load districts");
      } finally {
        setLoader(false);
      }
    };
    fetchDistricts();
  }, []);

  // ✅ Validation: only district → tehsil → uc are required
  const validationSchema = Yup.object({
    deh_name: Yup.string().required("Deh Name is required"),
    district_id: Yup.string().required("District is required"),
    tehsil_id: Yup.string().required("Tehsil is required"),
    //uc_id: Yup.string().required("UC is required"),
    status: Yup.number().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      deh_name: "",
      district_id: "",
      tehsil_id: "",
      //uc_id: "",
      status: 1,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);

      const data = {
        deh_name: values.deh_name,
        district_id: values.district_id,
        tehsil_id: values.tehsil_id,
        //uc_id: values.uc_id,
        status: values.status,
      };

      try {
        await fetchData(
          "dehs",
          "POST",
          data,
          {},
          {},
          navigate,
          "/dehs",
          dispatch
        );
        toast.success("Deh created successfully");
        navigate("/dehs");
      } catch (error) {
        toast.error(error.message || "Failed to create Deh");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // ✅ Handle district → fetch tehsils
  const handleDistrictChange = async (selectedValue) => {
    formik.setValues((prev) => ({
      ...prev,
      district_id: selectedValue,
      tehsil_id: "",
      uc_id: "",
    }));
    if (selectedValue) {
      setLoader(true);
      try {
        const res = await fetchData(
          "tehsils",
          "GET",
          null,
          {},
          { district_id: selectedValue },
          navigate,
          "/tehsils",
          dispatch
        );
        setTehsils(res?.data || []);
        setUcs([]);
      } catch (error) {
        toast.error("Failed to load tehsils");
      } finally {
        setLoader(false);
      }
    } else {
      setTehsils([]);
      setUcs([]);
    }
  };

  // ✅ Handle tehsil → fetch UCs
  // const handleTehsilChange = async (selectedValue) => {
  //   formik.setFieldValue("tehsil_id", selectedValue);
  //   formik.setFieldValue("uc_id", "");
  //   if (selectedValue) {
  //     setLoader(true);
  //     try {
  //       const res = await fetchData(
  //         "ucs",
  //         "GET",
  //         null,
  //         {},
  //         { tehsil_id: selectedValue },
  //         navigate,
  //         "/ucs",
  //         dispatch
  //       );
  //       setUcs(res?.data || []);
  //     } catch (error) {
  //       toast.error("Failed to load UCs");
  //     } finally {
  //       setLoader(false);
  //     }
  //   } else {
  //     setUcs([]);
  //   }
  // };

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Deh
        </h1>
        <BackButton text="Back" to="/dehs" />
      </div>
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Create Deh
          </h1>
        </div>
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4 p-4 my-4">
            {/* ❌ Province dropdown removed */}
            {/* ❌ Division dropdown removed */}
            {/* ❌ Deh Code removed */}

            <ThinDropdownWithSearch
              label="District"
              options={districts.map((d) => ({
                value: d.district_id || d.district_id,
                label: d.district_name || d.district_name,
              }))}
              value={formik.values.district_id}
              onChange={handleDistrictChange}
              error={formik.touched.district_id && formik.errors.district_id}
              helperText={
                formik.touched.district_id && formik.errors.district_id
              }
            />

            <ThinDropdownWithSearch
              label="Tehsil"
              options={tehsils.map((t) => ({
                value: t.tehsil_id || t.tehsil_id,
                label: t.tehsil_name || t.tehsil_name,
              }))}
              value={formik.values.tehsil_id}
              onChange={(val) => formik.setFieldValue("tehsil_id", val)}
              disabled={!formik.values.district_id}
              error={formik.touched.tehsil_id && formik.errors.tehsil_id}
              helperText={
                formik.touched.tehsil_id && formik.errors.tehsil_id
              }
            />

            {/* <ThinDropdownWithSearch
              label="UC"
              options={ucs.map((u) => ({
                value: u.uc_id,
                label: u.uc_name,
              }))}
              value={formik.values.uc_id}
              onChange={(val) => formik.setFieldValue("uc_id", val)}
              disabled={!formik.values.tehsil_id}
              error={formik.touched.uc_id && formik.errors.uc_id}
            /> */}

            <ThinTextField
              label="Deh Name"
              name="deh_name"
              value={formik.values.deh_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.deh_name && formik.errors.deh_name}
              helperText={
                formik.touched.deh_name && formik.errors.deh_name
              }
            />

            <ThinDropdownWithSearch
              label="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(val) => formik.setFieldValue("status", val)}
              error={formik.touched.status && formik.errors.status}
            />
          </div>

          <div className="p-4">
            <AddButton
              text="Submit"
              type="Submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeh;
